import { Home, ArrowRight, Menu, X, Shield, BookOpen } from 'lucide-react';
import JWTStatus from './JWTStatus';
import { LanguageToggle } from './LanguageToggle';
import { type Language } from '../i18n';
import i18n from '../i18n';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const MOBILE_LANGUAGES: { code: Language; name: string; flag: string }[] = [
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'en', name: 'English', flag: '🇺🇸' },
];

interface HeaderProps {
  onGoHome?: () => void;
  showHomeButton?: boolean;
  onGoToJWTDebug?: () => void;
  useNewDesign?: boolean;
  onUseNewDesignChange?: (useNewDesign: boolean) => void;
  // Пропсы для выбора версий посадочной страницы
  currentLanding?: string;
  onLandingChange?: (landing: string) => void;
  theme?: 'dark' | 'light';
  isLanding?: boolean; // Флаг, что мы на главной странице (лендинге)
  language?: Language;
  onLanguageChange?: (language: Language) => void;
}

export default function Header({ 
  onGoHome, 
  showHomeButton = false, 
  onGoToJWTDebug,
  useNewDesign = false,
  onUseNewDesignChange,
  currentLanding,
  onLandingChange,
  theme = 'light',
  isLanding = false,
  language: languageProp,
  onLanguageChange
}: HeaderProps) {
  const { t, i18n } = useTranslation('common');
  const lang = (languageProp ?? (i18n.language === 'en' ? 'en' : 'ru')) as Language;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  // На лендинге всегда используем светлую тему для всех элементов
  const isDark = theme === 'dark' && !isLanding;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileLanguageSelect = (newLang: Language) => {
    if (newLang === lang) {
      setIsMobileMenuOpen(false);
      return;
    }
    localStorage.setItem('plinus-language', newLang);
    onLanguageChange?.(newLang);
    void i18n.changeLanguage(newLang);
    setIsMobileMenuOpen(false);
    const path = window.location.pathname.replace(/^\/(ru|en)(\/.*)?$/, (_, __, rest) => rest || '/');
    if (/^\/share\//.test(path)) return;
    window.location.href = `/${newLang}${path}${window.location.search}${window.location.hash}`;
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`sticky top-0 z-50 transition-all duration-500 ease-out ${
      isScrolled 
        ? isDark
          ? 'bg-slate-800/80 backdrop-blur-xl shadow-lg border-b border-slate-700/40'
          : 'bg-white/80 backdrop-blur-xl shadow-lg border-b border-gray-200/40'
        : 'bg-transparent shadow-none border-b border-transparent'
    }`}>
      <div className="container mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-12 xl:px-16">
        <div className="flex items-center h-14 sm:h-16 lg:h-18 relative w-full">
          {/* Компактный логотип */}
          <a 
            href={`/${lang}/`}
            className="flex items-center flex-shrink-0 cursor-pointer"
            onClick={(e) => {
              if (onGoHome) {
                e.preventDefault();
                onGoHome();
              }
            }}
          >
            <img 
              src={isDark && !isLanding ? "/images/Plinus_FullLogo_W_H_52x160mm@4x.png" : "/images/Plinus_Valonik_Logo_Vector_2025.svg"}
              alt="ПЛИНУС - Анализ решений" 
              className="h-10 sm:h-12 lg:h-14 object-contain transition-all duration-300"
            />
          </a>
          <div className="flex items-center justify-end space-x-1 sm:space-x-4 flex-1 ml-auto">
            {/* JWT Status - только при включенной отладке */}
            {import.meta.env.VITE_JWT_DEBUG_ENABLED && (
              <JWTStatus className="hidden lg:block" />
            )}
            
            {/* JWT Debug кнопка - только при включенной отладке */}
            {import.meta.env.VITE_JWT_DEBUG_ENABLED && onGoToJWTDebug && (
              <button 
                onClick={onGoToJWTDebug}
                className={`inline-flex items-center px-2 py-1 text-xs transition-colors hidden lg:inline-flex ${
                  isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                }`}
                title="JWT Debug"
              >
                🔧 JWT
              </button>
            )}
            
            {/* Переключатель дизайна */}
            {onUseNewDesignChange && (
              <button 
                onClick={() => onUseNewDesignChange(!useNewDesign)}
                className={`inline-flex items-center px-2 py-1 text-xs transition-colors ${
                  useNewDesign 
                    ? isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                    : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                }`}
                title={`${useNewDesign ? 'Отключить' : 'Включить'} новый дизайн`}
              >
                {useNewDesign ? '🎨' : '📱'} {useNewDesign ? 'Новый' : 'Старый'}
              </button>
            )}

            {/* Мобильные иконки */}
            <div className="flex items-center space-x-1 sm:hidden">
              {showHomeButton && onGoHome && (
                <button 
                  onClick={onGoHome}
                  className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${
                    isDark 
                      ? 'hover:bg-slate-700 text-gray-400 hover:text-gray-200' 
                      : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                  }`}
                  title={t('home')}
                >
                  <Home className="w-4 h-4" />
                </button>
              )}
              <a 
                href="https://t.me/PlinusBot" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hidden min-[420px]:inline-flex items-center gap-2 px-3 py-2 bg-[#0088cc] text-white rounded-lg hover:bg-[#0077b3] transition-all duration-200 shadow-sm hover:shadow-md"
                title="Telegram Bot"
              >
                <span className="text-sm font-medium">@PlinusBot</span>
              </a>
            </div>

            {/* Десктопные кнопки */}
            <div className="hidden sm:flex items-center justify-end space-x-4 flex-1">
              {showHomeButton && onGoHome && (
                <button 
                  onClick={onGoHome}
                  className={`inline-flex items-center px-3 py-2 text-sm transition-colors ${
                    isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Home className="w-4 h-4 mr-1" />
                  {t('home')}
                </button>
              )}
              {onLanguageChange && (
                <LanguageToggle
                  currentLanguage={lang}
                  onLanguageChange={onLanguageChange}
                  variant={isDark ? 'dark' : 'light'}
                />
              )}
              <a 
                href="https://t.me/PlinusBot" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 bg-[#0088cc] text-white rounded-lg hover:bg-[#0077b3] transition-all duration-200 shadow-sm hover:shadow-md"
                title="Telegram Bot"
              >
                <span className="text-sm font-medium">@PlinusBot</span>
              </a>
            </div>
            
            {/* Главная кнопка "Решить вопрос" */}
            <a 
              href={`/${lang}/analysis`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 sm:px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 active:bg-primary-800 transition-all duration-200"
            >
              <span className="hidden sm:inline">{t('solveQuestion')}</span>
              <span className="sm:hidden">{t('solveQuestion').split(' ')[0]}</span>
              <ArrowRight className="w-4 h-4 ml-1 sm:ml-2" />
            </a>

            {/* Бургер-меню */}
            <button
              onClick={toggleMobileMenu}
              className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors sm:hidden ${
                isDark 
                  ? 'hover:bg-slate-700 text-gray-400 hover:text-gray-200' 
                  : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
              }`}
              title="Меню"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Мобильное меню */}
        {isMobileMenuOpen && (
          <div className={`sm:hidden border-t ${
            isDark 
              ? 'border-slate-700 bg-slate-800' 
              : 'border-gray-200 bg-white'
          }`}>
            <div className="px-3 sm:px-4 py-3 space-y-2">
              {onLanguageChange && MOBILE_LANGUAGES.map((language) => (
                <button
                  key={language.code}
                  type="button"
                  onClick={() => handleMobileLanguageSelect(language.code)}
                  className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors text-left ${
                    isDark
                      ? 'text-gray-300 hover:text-white hover:bg-slate-700'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <span className="w-4 h-4 mr-3 flex items-center justify-center text-lg flex-shrink-0" aria-hidden>
                    {language.flag}
                  </span>
                  <span>{language.name}</span>
                </button>
              ))}
              <a
                href={`/${lang}/terms-of-use`}
                className={`flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                  isDark
                    ? 'text-gray-300 hover:text-white hover:bg-slate-700'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <BookOpen className={`w-4 h-4 mr-3 ${
                  isDark ? 'text-gray-400' : 'text-gray-500'
                }`} />
                {t('termsOfUse')}
              </a>
              <a
                href={`/${lang}/privacy-policy`}
                className={`flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                  isDark
                    ? 'text-gray-300 hover:text-white hover:bg-slate-700'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Shield className={`w-4 h-4 mr-3 ${
                  isDark ? 'text-gray-400' : 'text-gray-500'
                }`} />
                {t('privacyPolicy')}
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
} 